import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Star } from "./brand/Logo";

/** Scroll-triggered fade/rise. */
export const Reveal = ({ children, delay = 0, y = 28, className = "", as = "div" }) => {
  const M = motion[as];
  return (
    <M
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay, ease: [0.2, 0.8, 0.2, 1] }}
    >
      {children}
    </M>
  );
};

/** Big headline that reveals line by line from a mask. */
export const SplitLines = ({ lines, className = "", lineClass = () => "", delay = 0, immediate = false }) => (
  <span className={className}>
    {lines.map((l, i) => (
      <span key={l + i} className="-mt-[0.16em] block overflow-hidden pb-[0.06em] pt-[0.16em]">
        <motion.span
          className={`block ${lineClass(i)}`}
          initial={{ y: "105%" }}
          {...(immediate ? { animate: { y: 0 } } : { whileInView: { y: 0 }, viewport: { once: true } })}
          transition={{ duration: 1, delay: delay + i * 0.09, ease: [0.2, 0.8, 0.2, 1] }}
        >
          {l}
        </motion.span>
      </span>
    ))}
  </span>
);

export const SectionLabel = ({ children, index, className = "", tone = "text-mist" }) => (
  <div className={`label flex items-center gap-3 ${tone} ${className}`}>
    <Star className="h-3 w-3 text-lav" />
    {index && <span className="text-lav">{index}</span>}
    <span>{children}</span>
  </div>
);

export const StarDivider = ({ className = "" }) => (
  <div className={`flex items-center gap-4 ${className}`} aria-hidden="true">
    <span className="h-px flex-1 bg-white/10" />
    <Star className="star-spin h-4 w-4 text-lav" />
    <span className="h-px flex-1 bg-white/10" />
  </div>
);

export const Marquee = ({ items, className = "" }) => (
  <div className={`overflow-hidden border-y border-white/10 py-5 ${className}`} data-testid="services-marquee">
    <div className="marquee">
      {[0, 1].map((k) => (
        <div key={k} className="flex shrink-0 items-center">
          {[...items, ...items].map((it, i) => (
            <span key={`${k}-${i}`} className="flex items-center font-display text-3xl font-semibold uppercase text-ivory md:text-5xl">
              <span className={i % 2 ? "text-stroke" : ""}>{it}</span>
              <Star className="mx-8 h-6 w-6 text-lav md:h-8 md:w-8" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

/** Lavender star cursor follower (fine pointers only). */
export const StarCursor = () => {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!window.matchMedia("(pointer: fine)").matches) return undefined;
    setOn(true);
    document.body.classList.add("has-star-cursor");
    const pos = { x: -100, y: -100, tx: -100, ty: -100, s: 1, ts: 1 };
    let raf = 0;
    const loop = () => {
      pos.x += (pos.tx - pos.x) * 0.3;
      pos.y += (pos.ty - pos.y) * 0.3;
      pos.s += (pos.ts - pos.s) * 0.2;
      if (ref.current) ref.current.style.transform = `translate3d(${pos.x - 9}px, ${pos.y - 9}px, 0) scale(${pos.s}) rotate(${pos.s * 45 - 45}deg)`;
      const settled = Math.abs(pos.tx - pos.x) < 0.1 && Math.abs(pos.ty - pos.y) < 0.1 && Math.abs(pos.ts - pos.s) < 0.01;
      raf = settled ? 0 : requestAnimationFrame(loop);
    };
    const move = (e) => {
      pos.tx = e.clientX;
      pos.ty = e.clientY;
      pos.ts = e.target.closest?.("a,button,[role=button],input,textarea,[role=combobox]") ? 2.2 : 1;
      if (!raf) raf = requestAnimationFrame(loop);
    };
    window.addEventListener("pointermove", move, { passive: true });
    return () => {
      window.removeEventListener("pointermove", move);
      cancelAnimationFrame(raf);
      document.body.classList.remove("has-star-cursor");
    };
  }, []);
  if (!on) return null;
  return (
    <div ref={ref} className="pointer-events-none fixed left-0 top-0 z-[100] h-[18px] w-[18px] will-change-transform" aria-hidden="true">
      <Star className="h-full w-full" color="#B794F6" />
    </div>
  );
};

/** First-load star mark. */
export const Preloader = () => {
  const [show, setShow] = useState(true);
  useEffect(() => {
    const id = setTimeout(() => setShow(false), 1100);
    return () => clearTimeout(id);
  }, []);
  return (
    <motion.div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-ink"
      initial={{ opacity: 1 }}
      animate={{ opacity: show ? 1 : 0 }}
      transition={{ duration: 0.6 }}
      style={{ pointerEvents: show ? "auto" : "none" }}
      data-testid="preloader"
    >
      <motion.div initial={{ scale: 0.3, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}>
        <Star className="h-16 w-16 text-lav" cut />
      </motion.div>
    </motion.div>
  );
};
